import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone, Building2, MapPin, Edit } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useGetClinics } from '../hooks/useApi';
import { isSuperAdmin } from '../utils/permissions';
import { useGetProfile } from '../hooks/useApi';

const AnimatedProfileSheet = ({ open, onClose }) => {
  const { currentUser } = useAuth();
  const panelRef = useRef(null);
  
  // Only fetch clinics for superadmin (they need to see all clinics)
  // For clinic admin/staff, clinic info should come from user object or a different endpoint
  const { data: clinicsData, isLoading } = useGetClinics({
    enabled: isSuperAdmin(currentUser) && open, // Only fetch if superadmin and sheet is open
  });
  
  const clinics = clinicsData?.data?.clinics || [];
  const userClinic = currentUser?.clinicId 
    ? (isSuperAdmin(currentUser) 
        ? clinics.find(c => c.id === currentUser.clinicId)
        : null) // For non-superadmin, clinic info should be in user object
    : null;

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when sheet is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [open, onClose]);

  // Split text animation for name
  const splitText = (text) => {
    if (!text) return [];
    return text.split('').map((char, index) => (
      <motion.span
        key={index}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.3,
          delay: index * 0.02,
          ease: [0.25, 0.1, 0.25, 1],
        }}
        className="inline-block"
      >
        {char === ' ' ? '\u00A0' : char}
      </motion.span>
    ));
  };

  const getRoleDisplay = (role) => {
    switch (role) {
      case 'superadmin':
        return 'Super Admin';
      case 'clinic_admin':
        return 'Doctor';
      case 'staff':
        return 'Staff';
      default:
        return role;
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  
    const {data: profile, Loading, error} = useGetProfile();
    const profileData = profile?.data?.[0];

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Panel */}
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="profile-sheet-title"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{
              type: 'spring',
              damping: 30,
              stiffness: 300,
            }}
            className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-white/90 backdrop-blur-xl shadow-2xl z-50 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10"
              aria-label="Close profile"
            >
              <X className="w-5 h-5 text-gray-700" />
            </motion.button>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 pt-16">
              {/* Clinic Logo */}
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  type: 'spring',
                  damping: 15,
                  stiffness: 200,
                  delay: 0.1,
                }}
                className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg"
              >
                <span className="text-3xl font-bold text-white">
                  {getInitials(profileData?.name || 'User')}
                </span>
              </motion.div>

              {/* Name with Split Text Animation */}
              <motion.h2
                id="profile-sheet-title"
                className="text-2xl font-bold text-gray-900 text-center mb-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {splitText(profileData?.name || 'User')}
              </motion.h2>

              {/* Role */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-center mb-8"
              >
                <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-sm font-medium">
                  {getRoleDisplay(profileData?.role === 1 ? 'Super Admin' : profileData?.role === 2 ? 'Doctor' : 'Staff')}
                </span>
              </motion.div>

              {/* Info Sections with Staggered Animation */}
              <div className="space-y-4">
                {/* Phone */}
                {(profileData?.phone) && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                    className="flex items-center gap-3 p-4 rounded-xl bg-gray-50/50 backdrop-blur-sm"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 mb-0.5">Phone</p>
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {profileData?.phone}
                      </p>
                    </div>
                  </motion.div>
                )}

                {/* Clinic Name */}
                {profileData?.clinic_name && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 }}
                    className="flex items-center gap-3 p-4 rounded-xl bg-gray-50/50 backdrop-blur-sm"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 mb-0.5">Clinic</p>
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {profileData?.clinic_name}
                      </p>
                    </div>
                  </motion.div>
                )}

                {/* Address */}
                {profileData?.address && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.7 }}
                    className="flex items-start gap-3 p-4 rounded-xl bg-gray-50/50 backdrop-blur-sm"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 mb-0.5">Address</p>
                      <p className="text-sm font-medium text-gray-900">
                        {profileData?.address}
                      </p>
                    </div>
                  </motion.div>
                )}

                {/* Email */}
                {profileData?.email && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.8 }}
                    className="flex items-center gap-3 p-4 rounded-xl bg-gray-50/50 backdrop-blur-sm"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <svg
                        className="w-5 h-5 text-primary-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 mb-0.5">Email</p>
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {profileData?.email}
                      </p>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>

            
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AnimatedProfileSheet;

